import React, { useEffect, useState } from "react";
import { CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from "@/components/ui/command";
import api from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { FileText, Users, PackageOpen, BookOpen, Landmark, ScrollText, ShoppingBag } from "lucide-react";
import { fmtINR } from "@/lib/format";

export default function GlobalSearch({ open, onOpenChange }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    if (!q || q.length < 2) { setResults({}); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/search", { params: { q } });
        setResults(r.data);
      } catch (e) { console.error(e); }
    }, 220);
    return () => clearTimeout(t);
  }, [q]);

  const go = (path) => { onOpenChange(false); navigate(path); };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput
        placeholder="Search across vouchers, invoices, parties, items, accounts…"
        value={q}
        onValueChange={setQ}
        data-testid="global-search-input"
      />
      <CommandList>
        <CommandEmpty>{q ? "No results." : "Type to search across the whole platform."}</CommandEmpty>
        {(results.vouchers || []).length > 0 && (
          <CommandGroup heading="Vouchers">
            {results.vouchers.map((v) => (
              <CommandItem key={v.id} onSelect={() => go(`/app/vouchers?id=${v.id}`)} data-testid={`search-v-${v.id}`}>
                <ScrollText className="w-4 h-4 mr-2 text-muted-foreground" />
                <span className="font-mono text-xs">{v.voucher_number}</span>
                <span className="ml-2 text-xs text-muted-foreground">{v.voucher_type} · {v.date}</span>
                <span className="ml-auto font-mono text-xs">{fmtINR(v.total_debit)}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {(results.invoices || []).length > 0 && (
          <CommandGroup heading="Sales Invoices">
            {results.invoices.map((v) => (
              <CommandItem key={v.id} onSelect={() => go(`/app/sales`)}>
                <FileText className="w-4 h-4 mr-2 text-muted-foreground" />
                <span className="font-mono text-xs">{v.invoice_number}</span>
                <span className="ml-auto font-mono text-xs">{fmtINR(v.grand_total)}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {(results.bills || []).length > 0 && (
          <CommandGroup heading="Purchase Bills">
            {results.bills.map((v) => (
              <CommandItem key={v.id} onSelect={() => go(`/app/purchases`)}>
                <ShoppingBag className="w-4 h-4 mr-2 text-muted-foreground" />
                <span className="font-mono text-xs">{v.bill_number}</span>
                <span className="ml-auto font-mono text-xs">{fmtINR(v.grand_total)}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {(results.parties || []).length > 0 && (
          <CommandGroup heading="Customers & Vendors">
            {results.parties.map((p) => (
              <CommandItem key={p.id} onSelect={() => go(p.party_type === "customer" ? "/app/customers" : "/app/vendors")}>
                <Users className="w-4 h-4 mr-2 text-muted-foreground" />
                <span>{p.name}</span>
                <span className="ml-2 text-xs text-muted-foreground">{p.party_type}</span>
                <span className="ml-auto font-mono text-xs">{fmtINR(p.balance)}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {(results.items || []).length > 0 && (
          <CommandGroup heading="Items">
            {results.items.map((i) => (
              <CommandItem key={i.id} onSelect={() => go("/app/inventory")}>
                <PackageOpen className="w-4 h-4 mr-2 text-muted-foreground" />
                <span>{i.name}</span>
                <span className="ml-2 text-xs text-muted-foreground font-mono">{i.sku}</span>
                <span className="ml-auto text-xs font-mono">{i.stock_on_hand}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {(results.accounts || []).length > 0 && (
          <CommandGroup heading="Accounts (COA)">
            {results.accounts.map((a) => (
              <CommandItem key={a.id} onSelect={() => go(`/app/accounts?id=${a.id}`)}>
                <BookOpen className="w-4 h-4 mr-2 text-muted-foreground" />
                <span className="font-mono text-xs">{a.code}</span>
                <span className="ml-2">{a.name}</span>
                <span className="ml-auto font-mono text-xs">{fmtINR(a.balance)}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {(results.assets || []).length > 0 && (
          <CommandGroup heading="Fixed Assets">
            {results.assets.map((a) => (
              <CommandItem key={a.id} onSelect={() => go("/app/assets")}>
                <Landmark className="w-4 h-4 mr-2 text-muted-foreground" />
                <span className="font-mono text-xs">{a.asset_code}</span>
                <span className="ml-2">{a.name}</span>
                <span className="ml-auto font-mono text-xs">{fmtINR(a.cost)}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  );
}
